import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Search, Brain, Lightbulb, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HowItWorks = () => {
  const navigate = useNavigate();

  const wasteExamples = [
    {
      waste: "Banana Peels",
      transformations: [
        "Eco-friendly paper and packaging",
        "Natural water purifiers",
        "Organic fertilizer and compost",
        "Bio-plastic alternatives"
      ]
    },
    {
      waste: "Coffee Grounds",
      transformations: [
        "Biodiesel fuel production",
        "Natural skincare products",
        "Mushroom cultivation substrate",
        "Odor neutralizers and air filters"
      ]
    },
    {
      waste: "E-Waste (Electronics)",
      transformations: [
        "Precious metal recovery (gold, silver, copper)",
        "Recycled plastic components",
        "Refurbished electronics for schools",
        "Art installations and sculptures"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#f8f6f0] py-12 px-6 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-5xl mx-auto"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#2E4E3F] hover:text-[#1E2B22] mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </button>

        <div className="bg-[#FDFBF7] rounded-3xl border border-[#E6E2D6] p-8 md:p-12 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h1 
            className="text-4xl md:text-5xl font-bold text-[#1E2B22] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            How It Works
          </h1>
          
          <p className="text-lg text-[#4A5E51] mb-12 leading-relaxed">
            Waste Intelligence Engine uses advanced AI to analyze your waste and discover proven transformation opportunities from research, startups, and innovations worldwide.
          </p>

          {/* Step-by-Step Process */}
          <div className="space-y-8 mb-16">
            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center text-2xl font-bold">
                  1
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Search className="w-6 h-6 text-[#B4654A]" />
                  <h2 className="text-2xl font-semibold text-[#2E4E3F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Enter Your Waste Type
                  </h2>
                </div>
                <p className="text-[#4A5E51] leading-relaxed mb-4">
                  Simply type any waste material you generate - whether it's industrial, agricultural, household, or electronic. Be as specific as you want: "coffee grounds", "textile scraps", "used cooking oil", "plastic bottles", or even "sugarcane bagasse".
                </p>
                <div className="bg-[#f8f6f0] p-4 rounded-lg border border-[#E6E2D6]">
                  <p className="text-sm text-[#4A5E51]">
                    <strong>Pro tip:</strong> The more specific you are, the more targeted your results will be. Instead of just "plastic", try "PET plastic bottles" or "polystyrene packaging".
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center text-2xl font-bold">
                  2
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Brain className="w-6 h-6 text-[#B4654A]" />
                  <h2 className="text-2xl font-semibold text-[#2E4E3F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    AI Analyzes Global Research
                  </h2>
                </div>
                <p className="text-[#4A5E51] leading-relaxed mb-4">
                  Our AI (powered by Claude Sonnet 4.5) scans its extensive knowledge base of research papers, patents, startup innovations, and circular economy case studies from around the world. It identifies proven methods and emerging technologies for transforming your specific waste type.
                </p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-[#f8f6f0] p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#2E4E3F] mb-1">1000s</p>
                    <p className="text-xs text-[#4A5E51]">Research Papers</p>
                  </div>
                  <div className="bg-[#f8f6f0] p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#2E4E3F] mb-1">100s</p>
                    <p className="text-xs text-[#4A5E51]">Startups & Innovations</p>
                  </div>
                  <div className="bg-[#f8f6f0] p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-[#2E4E3F] mb-1">Global</p>
                    <p className="text-xs text-[#4A5E51]">Case Studies</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center text-2xl font-bold">
                  3
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Lightbulb className="w-6 h-6 text-[#B4654A]" />
                  <h2 className="text-2xl font-semibold text-[#2E4E3F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Get Transformation Ideas
                  </h2>
                </div>
                <p className="text-[#4A5E51] leading-relaxed mb-4">
                  Receive 4-6 credible transformation ideas, each with:
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#2E4E3F] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5E51]"><strong>Innovation Title:</strong> Clear description of the transformation</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#2E4E3F] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5E51]"><strong>Detailed Description:</strong> 2-3 sentences explaining the process and benefits</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#2E4E3F] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5E51]"><strong>Category Tags:</strong> Bio-material, Energy, Paper, Digital, Circular Economy, etc.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#2E4E3F] flex-shrink-0 mt-0.5" />
                    <span className="text-[#4A5E51]"><strong>Credible Sources:</strong> Links to research papers, companies, or organizations</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-full bg-[#2E4E3F] text-[#FDFBF7] flex items-center justify-center text-2xl font-bold">
                  4
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-6 h-6 text-[#B4654A]" />
                  <h2 className="text-2xl font-semibold text-[#2E4E3F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Discover Partnership Opportunities
                  </h2>
                </div>
                <p className="text-[#4A5E51] leading-relaxed mb-4">
                  Every result includes "The Kaagazz Connection" - a unique section that explores how your waste could become raw material for eco-friendly products, particularly Kaagazz's sustainable paper and stationery line.
                </p>
                <div className="bg-[#2E4E3F] text-[#FDFBF7] p-6 rounded-2xl">
                  <p className="leading-relaxed opacity-90">
                    We believe every waste stream has potential for partnership. Whether your waste can be directly converted into paper products, or you can collaborate in other ways, we help you see the possibilities.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Real Examples */}
          <div className="border-t border-[#E6E2D6] pt-12">
            <h2 className="text-3xl font-bold text-[#2E4E3F] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
              Real Transformation Examples
            </h2>
            
            <div className="space-y-8">
              {wasteExamples.map((example, idx) => (
                <div key={idx} className="bg-[#f8f6f0] p-6 rounded-2xl border border-[#E6E2D6]">
                  <h3 className="text-xl font-semibold text-[#2E4E3F] mb-4">
                    {example.waste}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {example.transformations.map((transformation, tIdx) => (
                      <div key={tIdx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#B4654A] flex-shrink-0 mt-0.5" />
                        <span className="text-[#4A5E51]">{transformation}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center bg-[#2E4E3F] text-[#FDFBF7] p-8 rounded-3xl">
            <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to Discover What Your Waste is Worth?
            </h3>
            <p className="mb-6 opacity-90">
              Start analyzing your waste materials now and unlock hidden value.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#FDFBF7] text-[#2E4E3F] px-8 py-3 rounded-full font-medium hover:bg-[#f8f6f0] transition-colors"
            >
              Try Waste Intelligence Engine
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default HowItWorks;